<template>
    <div class="file-picker">
        <h2 v-if="title" class="file-picker-title">{{ title }}</h2>
        <div
            class="dropzone"
            :class="{ dragging: dragDepth > 0, 'has-file': selected != null, invalid: typeError != '' }"
            role="button"
            tabindex="0"
            @click="select"
            @keydown.enter.prevent="select"
            @keydown.space.prevent="select"
            @dragenter.prevent="dragDepth++"
            @dragover.prevent
            @dragleave.prevent="dragDepth--"
            @drop.prevent="onDrop"
        >
            <input ref="input" type="file" class="file-input" :multiple="isMultiple" :accept="filters.join(', ')" @change="inputChange"/>

            <template v-if="selected != null">
                <div class="file-badge">{{ extension }}</div>
                <div class="file-info">
                    <span class="file-name" :title="selected.name">{{ selected.name }}</span>
                    <span class="file-meta">{{ formattedSize }} · {{ $t('ClickOrDropToReplace') }}</span>
                </div>
                <button type="button" class="file-clear" :title="$t('RemoveFile')" @click.stop="clear">&times;</button>
            </template>

            <template v-else>
                <svg class="upload-icon" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M12 16V4m0 0l-4.5 4.5M12 4l4.5 4.5" />
                    <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
                </svg>
                <div class="file-info">
                    <span class="dropzone-text">{{ $t('DropFileOrBrowse') }}</span>
                    <span v-if="filters.length" class="file-meta">{{ $t('AllowedFileTypes') }}: {{ filters.join(', ') }}</span>
                </div>
            </template>
        </div>
        <span v-if="typeError != ''" class="file-error">{{ typeError }}</span>
    </div>
</template>

<script>
export default {
    props: {
        title: {},
        filters: {
            type: Array,
            default: () => []
        },
        isDirectory: {
            type: Boolean,
            default: false
        },
        isMultiple: {
            type: Boolean,
            default: false
        },
    },
    emits: ['changed'],
    data() {
        return {
            selected: null,
            dragDepth: 0,
            typeError: ''
        }
    },
    computed: {
        extension() {
            var parts = this.selected.name.split('.');
            return parts.length > 1 ? parts.pop().toUpperCase().slice(0, 4) : 'FILE';
        },
        formattedSize() {
            var size = this.selected.size;
            var units = ['B', 'KB', 'MB', 'GB'];
            var i = 0;
            while (size >= 1024 && i < units.length - 1) {
                size /= 1024;
                i++;
            }
            return `${i == 0 ? size : size.toFixed(1)} ${units[i]}`;
        }
    },
    methods: {
        select() {
            this.$refs.input.click();
        },
        inputChange(e) {
            this.pick(e.target.files[0]);
            // Reset so picking the same file again still fires 'change'.
            e.target.value = '';
        },
        onDrop(e) {
            this.dragDepth = 0;
            this.pick(e.dataTransfer.files[0]);
        },
        pick(file) {
            if (!file) {
                return;
            }
            // The native dialog lets users switch to "All files" and drops bypass
            // 'accept' entirely, so validate the extension ourselves.
            if (!this.matchesFilters(file)) {
                this.typeError = `${this.$t('InvalidFileType')}: ${file.name}`;
                return;
            }
            this.typeError = '';
            this.selected = file;
            this.$emit('changed', file);
        },
        clear() {
            this.selected = null;
            this.typeError = '';
            this.$emit('changed', null);
        },
        matchesFilters(file) {
            if (this.filters.length == 0) {
                return true;
            }
            var name = file.name.toLowerCase();
            return this.filters.some(f => {
                f = f.toLowerCase();
                if (f.startsWith('.')) {
                    return name.endsWith(f);
                }
                if (f.endsWith('/*')) {
                    return file.type.startsWith(f.slice(0, -1));
                }
                return file.type == f;
            });
        }
    }
}
</script>

<style scoped>
.file-picker {
    display: flex;
    flex-direction: column;
    width: 100%;
}

.file-picker-title {
    color: black;
}

.file-input {
    display: none;
}

.dropzone {
    display: flex;
    align-items: center;
    gap: 12px;
    width: 100%;
    min-height: 64px;
    padding: 12px 14px;
    border: 2px dashed #AEC4CF;
    border-radius: 6px;
    background-color: #ffffff;
    color: #414F51;
    cursor: pointer;
    text-align: left;
    transition: border-color 0.15s ease-in-out, background-color 0.15s ease-in-out;
    outline: none;
}

.dropzone:hover,
.dropzone:focus-visible,
.dropzone.dragging {
    border-color: #356C88;
    background-color: #e5f4f7;
}

.dropzone.has-file {
    border-style: solid;
}

.dropzone.invalid {
    border-color: #FD8282;
}

.upload-icon {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    fill: none;
    stroke: #356C88;
    stroke-width: 2;
    stroke-linecap: round;
    stroke-linejoin: round;
}

.file-badge {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 40px;
    height: 40px;
    border-radius: 6px;
    background-color: #356C88;
    color: white;
    font-size: 11px;
    font-weight: 700;
    letter-spacing: 0.5px;
}

.file-info {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    min-width: 0;
}

.dropzone-text,
.file-name {
    font-size: 14px;
    font-weight: 600;
    color: #272727;
}

.file-name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.file-meta {
    font-size: 12px;
    color: #7a8a8f;
}

.file-clear {
    flex-shrink: 0;
    width: 28px;
    height: 28px;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: transparent;
    box-shadow: none;
    color: #7a8a8f;
    font-size: 20px;
    line-height: 28px;
    cursor: pointer;
}

.file-clear:hover {
    background-color: #f1f1f1;
    color: #FF6666;
}

.file-error {
    margin-top: 4px;
    font-size: 12px;
    color: #FD8282;
}
</style>



// WEBPACK FOOTER //
// src/components/FilePicker.vue